import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface Profile {
  id: string;
  username: string;
  avatar_url: string;
  level: string;
  eco_coins: number;
  streak: number;
  location: string;
  co2_saved: number;
  waste_reduced: number;
  trees_planted: number;
  water_saved: number;
  energy_saved: number;
  created_at: string;
  updated_at: string;
  points: number;
  gender: string | null;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;
  uploadAvatar: (file: File) => Promise<{ error: any; url?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const mockProfile: Profile = {
  id: 'mock-user-1',
  username: 'EcoWarrior23',
  avatar_url: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
  level: 'Tree',
  eco_coins: 1247,
  streak: 12,
  location: 'San Francisco, CA',
  co2_saved: 45.2,
  waste_reduced: 23.8,
  trees_planted: 3,
  water_saved: 1240,
  energy_saved: 89.5,
  created_at: '2024-01-15T00:00:00Z',
  updated_at: '2024-12-08T00:00:00Z',
  points: 1247,
  gender: null
};

// Default avatar for users without uploaded avatar
const getDefaultAvatar = (username: string) => {
  const colors = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1'];
  const colorIndex = username.length % colors.length;
  const initials = username.substring(0, 2).toUpperCase();
  
  // Create a simple SVG avatar with initials
  const svg = `
    <svg width="150" height="150" xmlns="http://www.w3.org/2000/svg">
      <rect width="150" height="150" fill="${colors[colorIndex]}"/>
      <text x="75" y="85" font-family="Arial, sans-serif" font-size="48" font-weight="bold" text-anchor="middle" fill="white">${initials}</text>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Convert file to base64 data URL for fallback storage
const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    if (!isSupabaseConfigured) {
      console.log('Supabase not configured. Using mock data.');
      setProfile(mockProfile);
      setUser({
        id: 'mock-user-1',
        email: 'demo@greenstep.com'
      } as User);
      setLoading(false);
      return;
    }

    try {
      // Get initial session
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        await fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }

      // Listen for auth changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state changed:', event, session?.user?.id);
          setSession(session);
          setUser(session?.user ?? null);
          
          if (session?.user) {
            await fetchProfile(session.user.id);
          } else {
            setProfile(null);
            setLoading(false);
          }
        }
      );

      return () => subscription.unsubscribe();
    } catch (error) {
      console.error('Error initializing auth:', error);
      setLoading(false);
    }
  };

  const fetchProfile = async (userId: string) => {
    try {
      console.log('Fetching profile for user:', userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        setLoading(false);
      } else if (!data) {
        console.log('Profile not found, creating new profile...');
        await createProfile(userId);
      } else {
        console.log('Profile fetched successfully:', data);
        setProfile(data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoading(false);
    }
  };

  const createProfile = async (userId: string, username?: string) => {
    try {
      console.log('Creating profile for user:', userId);
      
      const defaultUsername = username || `EcoWarrior${userId.slice(0, 8)}`;
      
      const newProfile = {
        id: userId,
        username: defaultUsername,
        avatar_url: getDefaultAvatar(defaultUsername),
        level: 'Sprout',
        eco_coins: 0,
        streak: 0,
        location: '',
        co2_saved: 0,
        waste_reduced: 0,
        trees_planted: 0,
        water_saved: 0,
        energy_saved: 0,
        points: 0,
        gender: null
      };

      const { data, error } = await supabase
        .from('profiles')
        .upsert(newProfile, { onConflict: 'id' })
        .select()
        .single();

      if (error) {
        console.error('Error creating profile:', error);
        setLoading(false);
      } else {
        console.log('Profile created successfully:', data);
        setProfile(data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, username: string) => {
    if (!isSupabaseConfigured) {
      console.log('Mock sign up for:', email);
      const mockUser = {
        id: `mock-${Date.now()}`,
        email: email
      } as User;
      
      const newMockProfile = {
        ...mockProfile,
        id: mockUser.id,
        username: username,
        avatar_url: getDefaultAvatar(username),
        eco_coins: 0,
        streak: 0,
        level: 'Sprout',
        co2_saved: 0,
        waste_reduced: 0,
        trees_planted: 0,
        water_saved: 0,
        energy_saved: 0,
        points: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      setUser(mockUser);
      setProfile(newMockProfile);
      
      return { error: null };
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username
          }
        }
      });

      if (error) {
        console.error('Sign up error:', error);
        return { error };
      }

      // Create profile with the provided username
      if (data.user) {
        await createProfile(data.user.id, username);
      }

      return { error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      console.log('Mock sign in for:', email);
      const mockUser = {
        id: 'mock-user-1',
        email: email
      } as User;
      
      setUser(mockUser);
      setProfile(mockProfile);
      
      return { error: null };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Sign in error:', error);
      }

      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    if (!isSupabaseConfigured) {
      console.log('Mock sign out');
      setUser(null);
      setProfile(null);
      return;
    }

    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const uploadAvatar = async (file: File) => {
    if (!user) return { error: 'No user logged in' };

    if (!isSupabaseConfigured) {
      console.log('Mock avatar upload');
      // Create a temporary URL for the uploaded file
      const tempUrl = URL.createObjectURL(file);
      
      if (profile) {
        const updatedProfile = { ...profile, avatar_url: tempUrl };
        setProfile(updatedProfile);
      }
      
      return { error: null, url: tempUrl };
    }

    try {
      // First, try to upload to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Math.random()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) {
        console.warn('Supabase storage upload failed:', uploadError.message);
        
        // Fallback: Convert file to base64 data URL
        console.log('Using fallback: converting image to base64 data URL');
        const dataUrl = await fileToDataUrl(file);
        
        // Update profile with base64 data URL
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ avatar_url: dataUrl })
          .eq('id', user.id);

        if (updateError) {
          return { error: updateError };
        }

        if (profile) {
          setProfile({ ...profile, avatar_url: dataUrl });
        }

        return { error: null, url: dataUrl };
      }

      // If upload succeeded, get the public URL
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const avatarUrl = data.publicUrl;

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', user.id);

      if (updateError) {
        return { error: updateError };
      }

      if (profile) {
        setProfile({ ...profile, avatar_url: avatarUrl });
      }

      return { error: null, url: avatarUrl };
    } catch (error) {
      console.error('Error uploading avatar:', error);
      
      // Final fallback: use base64 data URL
      try {
        console.log('Using final fallback: converting image to base64 data URL');
        const dataUrl = await fileToDataUrl(file);
        
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ avatar_url: dataUrl })
          .eq('id', user.id);

        if (updateError) {
          return { error: updateError };
        }

        if (profile) {
          setProfile({ ...profile, avatar_url: dataUrl });
        }

        return { error: null, url: dataUrl };
      } catch (fallbackError) {
        console.error('Fallback avatar upload failed:', fallbackError);
        return { error: fallbackError };
      }
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: 'No user logged in' };

    if (!isSupabaseConfigured) {
      console.log('Mock profile update:', updates);
      if (profile) {
        setProfile({ ...profile, ...updates });
      }
      return { error: null };
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (!error && profile) {
        setProfile({ ...profile, ...updates });
      }

      return { error };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { error };
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    uploadAvatar
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}